import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CloseIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ToggleChip,
} from '@eventbrite/marmalade';
import { useIsMobileDevice } from '@eventbrite/legacy-infra.feature-detection';
import { defineMessages, useIntl } from 'react-intl';
import { withBasePath } from '@/utils/withBasePath';
import styles from './OrganizerCalendar.module.scss';

const TABLET_MAX_WIDTH = 1023;
const MONTHLY_EVENTS_STALE_TIME = 10 * 60 * 1000;

const messages = defineMessages({
  close: {
    id: 'Close calendar',
    defaultMessage: 'Close calendar',
  },
  open: {
    id: 'Open calendar',
    defaultMessage: 'Open calendar',
  },
  pickDate: {
    id: 'Pick date',
    defaultMessage: 'Pick date',
  },
  today: {
    id: 'Today',
    defaultMessage: 'Today',
  },
  yesterday: {
    id: 'Yesterday',
    defaultMessage: 'Yesterday',
  },
  tomorrow: {
    id: 'Tomorrow',
    defaultMessage: 'Tomorrow',
  },
  previousMonth: {
    id: 'Previous month',
    defaultMessage: 'Previous month',
  },
  nextMonth: {
    id: 'Next month',
    defaultMessage: 'Next month',
  },
  calendarToggle: {
    id: 'Selected date {dateLabel}. Toggle calendar',
    defaultMessage: 'Selected date {dateLabel}. Toggle calendar',
  },
});

interface Props {
  locale: string;
  organizerId: string;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
}

interface MonthlyEvent {
  id: string;
  startDate: string;
}

interface MonthlyEventsResponse {
  events?: MonthlyEvent[];
}

function getSafeLocale(locale: string) {
  const normalizedLocale = locale.replace(/_/g, '-');

  try {
    const [supportedLocale] = Intl.DateTimeFormat.supportedLocalesOf([normalizedLocale]);
    return supportedLocale ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

function createDateTimeFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const safeLocale = getSafeLocale(locale);

  try {
    return new Intl.DateTimeFormat(safeLocale, options);
  } catch {
    return new Intl.DateTimeFormat('en-US', options);
  }
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function addDays(date: Date, amount: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + amount);
  return nextDate;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getDateKeyFromEventStartDate(startDate: string) {
  const isoDatePart = startDate.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDatePart) {
    return isoDatePart;
  }

  const date = new Date(startDate);
  return Number.isNaN(date.getTime()) ? null : getDateKey(date);
}

function isSameMonth(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function getMonthDays(visibleMonth: Date) {
  const firstDay = startOfMonth(visibleMonth);
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - firstDay.getDay());

  const lastDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);

  const days: Date[] = [];
  const cursor = new Date(gridStart);
  while (cursor <= lastDay) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

function formatChipLabel(
  date: Date,
  today: Date,
  locale: string,
  labels: {
    today: string;
    yesterday: string;
    tomorrow: string;
  },
) {
  if (isSameDay(date, today)) {
    return labels.today;
  }

  if (isSameDay(date, addDays(today, -1))) {
    return labels.yesterday;
  }

  if (isSameDay(date, addDays(today, 1))) {
    return labels.tomorrow;
  }

  return createDateTimeFormatter(locale, {
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function formatDayAriaLabel(date: Date, locale: string) {
  return createDateTimeFormatter(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function getWeekdayLabels(locale: string) {
  const formatter = createDateTimeFormatter(locale, { weekday: 'short' });
  const sunday = new Date(Date.UTC(2024, 0, 7));

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(sunday);
    date.setUTCDate(sunday.getUTCDate() + index);
    return formatter.format(date).replace('.', '').slice(0, 2);
  });
}

async function fetchOrganizerMonthlyEvents(
  organizerId: string,
  month: Date,
): Promise<MonthlyEventsResponse> {
  const params = new URLSearchParams({
    month: String(month.getMonth() + 1),
    year: String(month.getFullYear()),
  });
  const res = await fetch(
    withBasePath(`/api/organizers/${organizerId}/monthly-events/?${params}`),
    {
      credentials: 'include',
    },
  );

  if (!res.ok) {
    throw new Error(`Monthly events request failed: ${res.status}`);
  }

  return (await res.json()) as MonthlyEventsResponse;
}

function useOrganizerMonthlyEvents(organizerId: string, month: Date, enabled: boolean) {
  const normalizedMonth = startOfMonth(month);
  const monthNumber = normalizedMonth.getMonth() + 1;
  const year = normalizedMonth.getFullYear();

  const query = useQuery({
    queryKey: ['organizer-monthly-events', organizerId, year, monthNumber],
    queryFn: () => fetchOrganizerMonthlyEvents(organizerId, normalizedMonth),
    enabled: enabled && Boolean(organizerId.trim()),
    staleTime: MONTHLY_EVENTS_STALE_TIME,
  });

  const eventDateKeys = useMemo(() => {
    const events = query.data?.events ?? [];

    return new Set(
      events
        .map((event) => getDateKeyFromEventStartDate(event.startDate))
        .filter((dateKey): dateKey is string => dateKey !== null),
    );
  }, [query.data]);

  return {
    eventDateKeys,
    isLoading: query.isLoading,
  };
}

interface CalendarGridProps {
  locale: string;
  month: Date;
  selectedDate: Date;
  today: Date;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onSelectDate: (date: Date) => void;
  eventDateKeys: Set<string>;
  isLoadingEvents: boolean;
}

function CalendarGrid({
  locale,
  month,
  selectedDate,
  today,
  onPreviousMonth,
  onNextMonth,
  onSelectDate,
  eventDateKeys,
  isLoadingEvents,
}: CalendarGridProps) {
  const intl = useIntl();
  const days = useMemo(() => getMonthDays(month), [month]);
  const weekdayLabels = useMemo(() => getWeekdayLabels(locale), [locale]);
  const monthLabel = useMemo(
    () =>
      createDateTimeFormatter(locale, {
        month: 'long',
        year: 'numeric',
      }).format(month),
    [locale, month],
  );

  return (
    <div
      className={styles.calendarCard}
      data-testid="organizer-calendar-panel"
      aria-busy={isLoadingEvents}
    >
      <div className={styles.calendarHeader}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onPreviousMonth}
          aria-label={intl.formatMessage(messages.previousMonth)}
        >
          <ChevronLeftIcon size="small" aria-hidden />
        </button>
        <div className={styles.monthLabel}>{monthLabel}</div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onNextMonth}
          aria-label={intl.formatMessage(messages.nextMonth)}
        >
          <ChevronRightIcon size="small" aria-hidden />
        </button>
      </div>

      <div className={styles.weekdayRow}>
        {weekdayLabels.map((label) => (
          <div key={label} className={styles.weekdayCell}>
            {label}
          </div>
        ))}
      </div>

      <div className={styles.daysGrid}>
        {days.map((day) => {
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, today);
          const hasEvent = !isLoadingEvents && eventDateKeys.has(getDateKey(day));
          const isEnabled = isSameMonth(day, month) && hasEvent;

          return (
            <button
              key={day.toISOString()}
              type="button"
              className={styles.dayButton}
              data-selected={isSelected}
              data-today={isToday}
              data-has-event={hasEvent}
              aria-pressed={isSelected}
              aria-label={formatDayAriaLabel(day, locale)}
              disabled={!isEnabled}
              onClick={() => onSelectDate(day)}
            >
              {isLoadingEvents ? (
                <span
                  key="skeleton"
                  className={styles.daySkeleton}
                  data-testid="calendar-day-skeleton"
                />
              ) : (
                <>
                  <span key="day" className={styles.dayCircle}>
                    {day.getDate()}
                  </span>
                  {hasEvent ? <span className={styles.eventDot} aria-hidden /> : null}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function OrganizerCalendar({ locale, organizerId, selectedDate, onSelectDate }: Props) {
  const intl = useIntl();
  const isTabletOrBelow = useIsMobileDevice(TABLET_MAX_WIDTH);
  const isDesktop = !isTabletOrBelow;
  const today = useMemo(() => startOfDay(new Date()), []);
  const [visibleMonth, setVisibleMonth] = useState<Date>(startOfMonth(today));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const shouldFetchMonthlyEvents = isDesktop || isCalendarOpen;
  const { eventDateKeys, isLoading } = useOrganizerMonthlyEvents(
    organizerId,
    visibleMonth,
    shouldFetchMonthlyEvents,
  );

  useEffect(() => {
    if (isDesktop) {
      setIsCalendarOpen(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    setVisibleMonth(startOfMonth(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    if (isDesktop || !isCalendarOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCalendarOpen, isDesktop]);

  const chipLabel = formatChipLabel(selectedDate, today, locale, {
    today: intl.formatMessage(messages.today),
    yesterday: intl.formatMessage(messages.yesterday),
    tomorrow: intl.formatMessage(messages.tomorrow),
  });
  const isSelectedDateToday = isSameDay(selectedDate, today);

  const handleSelectDate = (date: Date) => {
    setVisibleMonth(startOfMonth(date));
    onSelectDate(date);

    if (!isDesktop) {
      setIsCalendarOpen(false);
    }
  };

  const handleToggleCalendar = (pressed: boolean) => {
    setIsCalendarOpen(pressed);
  };

  const calendar = (
    <CalendarGrid
      locale={locale}
      month={visibleMonth}
      selectedDate={selectedDate}
      today={today}
      onPreviousMonth={() => setVisibleMonth((currentMonth) => addMonths(currentMonth, -1))}
      onNextMonth={() => setVisibleMonth((currentMonth) => addMonths(currentMonth, 1))}
      onSelectDate={handleSelectDate}
      eventDateKeys={eventDateKeys}
      isLoadingEvents={isLoading}
    />
  );

  const toggle = (
    <ToggleChip
      aria-label={intl.formatMessage(messages.calendarToggle, { dateLabel: chipLabel })}
      className={[styles.toggleChip, !isSelectedDateToday ? styles.toggleChipDateActive : '']
        .filter(Boolean)
        .join(' ')}
      wrapperClassName={[
        styles.toggleChipWrapper,
        !isSelectedDateToday ? styles.toggleChipWrapperDateActive : '',
      ]
        .filter(Boolean)
        .join(' ')}
      pressed={isCalendarOpen}
      onPressedChange={handleToggleCalendar}
      iconTreatment="alwaysShow"
      icon={
        <ChevronDownIcon
          size="small"
          aria-hidden
          className={isCalendarOpen ? styles.toggleIconOpen : styles.toggleIcon}
        />
      }
    >
      {chipLabel}
    </ToggleChip>
  );

  const responsiveToggle = (
    <div className={styles.mobileToggle} data-testid="organizer-calendar-mobile-toggle">
      {toggle}
      {isCalendarOpen ? (
        <div
          className={styles.mobileSheet}
          role="dialog"
          aria-modal="true"
          aria-label={intl.formatMessage(messages.pickDate)}
        >
          <div className={styles.mobileSheetHeader}>
            <div className={styles.mobileSheetTitle}>{intl.formatMessage(messages.pickDate)}</div>
            <button
              type="button"
              className={styles.mobileCloseButton}
              onClick={() => setIsCalendarOpen(false)}
              aria-label={intl.formatMessage(messages.close)}
            >
              <CloseIcon size="small" aria-hidden />
            </button>
          </div>
          <div className={styles.mobileSheetBody}>{calendar}</div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <div className={styles.desktopCalendar} data-testid="organizer-calendar-desktop">
        {calendar}
      </div>
      <div className={styles.responsiveToggle} data-testid="organizer-calendar-responsive">
        {responsiveToggle}
      </div>
    </>
  );
}

export default OrganizerCalendar;
